import { styled, keyframes } from "@mui/system";
import { Button } from "@mui/material";

// Diagonal shine streak that sweeps across the pill on hover. It moves by
// transform rather than `left`, so the sweep never repaints the pill: the
// streak is 45% of the pill wide and starts at left -60%, so crossing to the
// old end position (left 140%) is 200% of the pill = 444% of its own width.
const shineSweep = keyframes`
  0%   { transform: translateX(0) skewX(-20deg); }
  100% { transform: translateX(444.44%) skewX(-20deg); }
`;

// Gentle idle float so the pills feel alive while resting on the photo
const pillFloat = keyframes`
  0%   { transform: translateY(0px); }
  50%  { transform: translateY(-5px); }
  100% { transform: translateY(0px); }
`;

// Glassy white CTA pill with the legacy soft shadow + a shine sweep on hover.
// Used for the floating "View Work" / "Download CV" buttons on the hero image (mobile).
export const FloatingPill = styled(Button)(() => ({
  position: "relative",
  overflow: "hidden",
  borderRadius: "9999px",
  textTransform: "none",
  fontFamily: "Roboto, sans-serif",
  fontWeight: 600,
  fontSize: "13px",
  lineHeight: 1,
  letterSpacing: "0.2px",
  whiteSpace: "nowrap",
  padding: "9px 16px",
  minWidth: 0,
  color: "#111",
  // No backdrop blur: at 92% white nothing behind shows through to blur, and on
  // an element whose float animation moves it every frame the blur re-ran every
  // frame for nothing.
  background: "rgba(255, 255, 255, 0.92)",
  border: "1px solid rgba(255, 255, 255, 0.6)",
  boxShadow:
    "inset 2px 2px 2px 0 rgba(255,255,255,0.5), 7px 7px 20px 0 rgba(0,0,0,0.18), 4px 4px 5px 0 rgba(0,0,0,0.12)",
  transition: "transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease",
  animation: `${pillFloat} 5s ease-in-out infinite`,

  "& .MuiButton-startIcon": {
    marginRight: "6px",
    marginLeft: 0,
  },
  "& .MuiSvgIcon-root": {
    fontSize: "18px",
  },

  // White shine streak that sweeps across on hover
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-60%",
    width: "45%",
    height: "100%",
    background:
      "linear-gradient(110deg, transparent 0%, rgba(255,255,255,0.85) 50%, transparent 100%)",
    transform: "skewX(-20deg)",
    pointerEvents: "none",
  },

  // Note: the idle float animation drives `transform`, and a keyframe animation
  // overrides a rule-level transform - so hover feedback uses shadow/background/shine,
  // not translate, to avoid being silently ignored.
  "&:hover": {
    background: "rgba(255, 255, 255, 1)",
    boxShadow:
      "inset 2px 2px 2px 0 rgba(255,255,255,0.6), 9px 9px 26px 0 rgba(0,0,0,0.24), 5px 5px 7px 0 rgba(0,0,0,0.16)",
  },
  "&:hover::before": {
    animation: `${shineSweep} 0.85s ease`,
  },

  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
    "&:hover::before": { animation: "none" },
  },
}));
